import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import resultsService from '../../services/resultsService';
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import { groupsService } from '../../services';
import partnerTeamsService from '../../services/partnerTeamsService';
import TailwindPageWrapper from "../generic/TailwindPageWrapper";
import ConfirmationModal from "../admin/ConfirmationModal";
import GenericSelectTailwind from "../admin/GenericSelectTailwind";
import Toast from "../admin/Toast";

const ScorekeeperView = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Prüfe ob es sich um Admin-Bearbeitung handelt
  const isAdminEdit = searchParams.get('adminEdit') === 'true';
  const groupIdFromUrl = searchParams.get('groupId');

  // Venue des Scorekeepers - wenn keine zugewiesen, kann der User wählen
  const assignedVenueFromUser = user?.venue;
  const [selectedVenue, setSelectedVenue] = useState(assignedVenueFromUser || "");
  const assignedVenue = assignedVenueFromUser || selectedVenue;

  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingEventsWithCounts, setUpcomingEventsWithCounts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [partnerTeams, setPartnerTeams] = useState([]); // Partner Teams für Partnerform-Gruppen
  const [isPartnerForm, setIsPartnerForm] = useState(false);
  const [editedData, setEditedData] = useState(new Map()); // Map von participantId/partnerTeamId -> { judgeScores, penalty, finalScore }
  const [savedResults, setSavedResults] = useState(new Map()); // Map von participantId/partnerTeamId -> resultId
  const [isSaving, setIsSaving] = useState(false);
  const [savingParticipants, setSavingParticipants] = useState(new Set()); // Set von participantIds/partnerTeamIds die gerade gespeichert werden
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [participantStatuses, setParticipantStatuses] = useState(new Map()); // Map von participantId/partnerTeamId -> { didNotStart, isLate }
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [currentAthletes, setCurrentAthletes] = useState([null, null]); // [athlete1, athlete2] für Parallel-Modus
  const [currentRunIndex, setCurrentRunIndex] = useState(0); // Aktueller Run-Index für Run-basierte Athletenanzeige
  const [isManualSelection, setIsManualSelection] = useState(false); // Verhindert automatische Überschreibung bei manueller Auswahl
  const isManualSelectionRef = useRef(false); // Ref für synchrone Prüfung in useEffect (vermeidet Race Conditions)
  // Helper: Setzt isManualSelection synchron (Ref) und asynchron (State)
  const updateManualSelection = (value) => {
    isManualSelectionRef.current = value;
    setIsManualSelection(value);
  };
  const inputRefsMap = useRef(new Map()); // Map für Input-Referenzen: identifier-judgeIndex-digitIndex -> ref
  const athleteCardsRef = useRef(null); // Ref für die Eingabekarten – wird beim Bearbeiten gescrollt

  // localStorage-Funktionen für Persistenz der eingegebenen Punkte
  const getStorageKey = (groupId) => {
    return `scorekeeper_edited_data_group_${groupId}`;
  };

  // Speichere editedData in localStorage
  const saveEditedDataToStorage = (groupId, data) => {
    if (!groupId) return;
    try {
      const storageKey = getStorageKey(groupId);
      // Konvertiere Map zu Object für JSON-Serialisierung
      // Wichtig: Keys als Strings speichern, da Map-Keys auch Zahlen sein können
      const dataObject = {};
      data.forEach((value, key) => {
        // Konvertiere Key zu String für JSON (Map-Keys können Zahlen oder Strings sein)
        const keyStr = String(key);
        dataObject[keyStr] = value;
      });
      localStorage.setItem(storageKey, JSON.stringify(dataObject));
    } catch (err) {
      console.error('Fehler beim Speichern der eingegebenen Daten:', err);
    }
  };

  // Lade editedData aus localStorage
  const loadEditedDataFromStorage = (groupId) => {
    if (!groupId) return new Map();
    try {
      const storageKey = getStorageKey(groupId);
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const dataObject = JSON.parse(stored);
        // Konvertiere Object zurück zu Map
        // Wichtig: Keys können Zahlen oder Strings sein (participantId ist Zahl, partner-team-X ist String)
        const dataMap = new Map();
        Object.entries(dataObject).forEach(([keyStr, value]) => {
          // Versuche Key als Zahl zu parsen, falls es eine Zahl ist (z.B. participantId)
          // Ansonsten behalte als String (z.B. "partner-team-123")
          const key = /^\d+$/.test(keyStr) ? parseInt(keyStr, 10) : keyStr;
          dataMap.set(key, value);
        });
        return dataMap;
      }
    } catch (err) {
      console.error('Fehler beim Laden der eingegebenen Daten:', err);
    }
    return new Map();
  };

  // Lösche editedData aus localStorage
  const clearEditedDataFromStorage = (groupId) => {
    if (!groupId) return;
    try {
      const storageKey = getStorageKey(groupId);
      localStorage.removeItem(storageKey);
    } catch (err) {
      console.error('Fehler beim Löschen der eingegebenen Daten:', err);
    }
  };

  // Speichere editedData automatisch bei Änderungen
  useEffect(() => {
    if (currentEvent?.group?.id && editedData.size > 0) {
      saveEditedDataToStorage(currentEvent.group.id, editedData);
    }
  }, [editedData, currentEvent?.group?.id]);

  // Bereinige localStorage wenn sich die Gruppe ändert (alte Gruppe)
  const previousGroupIdRef = useRef(null);
  useEffect(() => {
    const currentGroupId = currentEvent?.group?.id;
    if (previousGroupIdRef.current && previousGroupIdRef.current !== currentGroupId) {
      // Gruppe hat sich geändert - alte Daten bleiben im localStorage für die alte Gruppe
      // (können später noch benötigt werden, falls zurück navigiert wird)
    }
    previousGroupIdRef.current = currentGroupId;
  }, [currentEvent?.group?.id]);

  useEffect(() => {
    if (isAdminEdit && groupIdFromUrl) {
      // Admin-Bearbeitung: Lade spezifische Gruppe
      loadDataForAdminEdit();
    } else if (assignedVenue) {
      // Normale Scorekeeper-Ansicht
      loadData();
    }
  }, [assignedVenue, isAdminEdit, groupIdFromUrl]);

  // Lade Daten für Admin-Bearbeitung
  const loadDataForAdminEdit = async () => {
    try {
      setLoading(true);

      // Lade alle Schedule-Events
      const scheduleResult = await scheduleService.getAllScheduleEvents({});
      const scheduleEvents = scheduleResult.success ? scheduleResult.data || [] : [];

      // Finde das Event mit der spezifischen groupId (vergleiche als String, da URL-Parameter Strings sind)
      const targetEvent = scheduleEvents.find(e => {
        const eventGroupId = String(e.group?.id || e.id || '');
        const urlGroupId = String(groupIdFromUrl || '');
        return eventGroupId === urlGroupId;
      });

      if (!targetEvent) {
        console.error('loadDataForAdminEdit - Event nicht gefunden für groupId:', groupIdFromUrl);
        console.log('loadDataForAdminEdit - Verfügbare Events:', scheduleEvents.map(e => ({
          id: e.id,
          groupId: e.group?.id,
          name: e.name || e.group?.name
        })));
        setLoading(false);
        return;
      }

      if (targetEvent) {
        setCurrentEvent(targetEvent);
        updateManualSelection(false); // Reset manuelle Auswahl bei neuem Event
        setCurrentRunIndex(0); // Reset Run-Index bei neuem Event

        // Lade Teilnehmer oder Partner Teams für diese Gruppe
        const isPartnerFormGroup = targetEvent.group?.category?.type === 'Partnerform';
        setIsPartnerForm(isPartnerFormGroup);

        if (isPartnerFormGroup) {
          // Für Partnerform: Lade Partner Teams
          const groupResponse = await groupsService.getGroupWithStartOrder(targetEvent.group.id);
          if (groupResponse.success && groupResponse.data?.partnerTeams) {
            const teams = groupResponse.data.partnerTeams.sort((a, b) => {
              const aStartNumber = a.startNumber || 999;
              const bStartNumber = b.startNumber || 999;
              return aStartNumber - bStartNumber;
            });
            setPartnerTeams(teams);
            setParticipants([]);
          } else {
            setPartnerTeams([]);
            setParticipants([]);
          }
        } else {
          // Für normale Gruppen: Lade Teilnehmer
          const participantsResult = await participantsService.getAllParticipants();
          if (participantsResult.success) {
            const allParticipants = participantsResult.data || [];
            const eventParticipants = allParticipants.filter(p => {
              if (!p.groups || !Array.isArray(p.groups)) return false;
              return p.groups.some(g => g.id === targetEvent.group.id);
            });

            eventParticipants.sort((a, b) => {
              const aGroup = a.groups.find(g => g.id === targetEvent.group.id);
              const bGroup = b.groups.find(g => g.id === targetEvent.group.id);
              const aStartNumber = aGroup ? aGroup.startNumber : 999;
              const bStartNumber = bGroup ? bGroup.startNumber : 999;
              return aStartNumber - bStartNumber;
            });

            setParticipants(eventParticipants);
            setPartnerTeams([]);
          }
        }

        // Lade bestehende Ergebnisse
        if (targetEvent.group.id) {
          await loadResults(targetEvent.group.id);
        }
      } else {
        addToast('error', 'Gruppe nicht gefunden');
        navigate('/admin/results');
      }

      setUpcomingEvents([]);
      setUpcomingEventsWithCounts([]);
    } catch (error) {
      console.error('Fehler beim Laden der Daten für Admin-Bearbeitung:', error);
      addToast('error', 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Lade Schedule-Events für die zugewiesene Fläche
      const scheduleResult = await scheduleService.getAllScheduleEvents({ venue: assignedVenue });
      const scheduleEvents = scheduleResult.success ? scheduleResult.data || [] : [];

      // Sortiere nach Startzeit
      scheduleEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

      // Finde aktuelles Event (Status "Aktiv")
      const activeEvent = scheduleEvents.find(e => e.status === 'Aktiv');
      setCurrentEvent(activeEvent || null);
      updateManualSelection(false); // Reset manuelle Auswahl bei neuem Event
      setCurrentRunIndex(0); // Reset Run-Index bei neuem Event

      // Finde nächste 2 Events (Status "Ausstehend" oder "Vorbereitung")
      const pendingEvents = scheduleEvents.filter(e => e.status === 'Ausstehend' || e.status === 'Vorbereitung').slice(0, 2);
      setUpcomingEvents(pendingEvents);

      // Lade Teilnehmeranzahl für kommende Events
      if (pendingEvents.length > 0) {
        const participantsResult = await participantsService.getAllParticipants();
        if (participantsResult.success) {
          const allParticipants = participantsResult.data || [];
          const eventsWithCounts = pendingEvents.map(event => {
            if (event.group?.id) {
              const eventParticipants = allParticipants.filter(p => {
                if (!p.groups || !Array.isArray(p.groups)) return false;
                return p.groups.some(g => g.id === event.group.id);
              });
              return {
                ...event,
                participantCount: eventParticipants.length
              };
            }
            return {
              ...event,
              participantCount: 0
            };
          });
          setUpcomingEventsWithCounts(eventsWithCounts);
        } else {
          setUpcomingEventsWithCounts(pendingEvents.map(e => ({ ...e, participantCount: 0 })));
        }
      } else {
        setUpcomingEventsWithCounts([]);
      }

      // Lade Teilnehmer oder Partner Teams für aktuelles Event
      if (activeEvent && activeEvent.group) {
        const isPartnerFormGroup = activeEvent.group.category?.type === 'Partnerform';
        setIsPartnerForm(isPartnerFormGroup);

        if (isPartnerFormGroup) {
          // Für Partnerform: Lade Partner Teams
          const groupResponse = await groupsService.getGroupWithStartOrder(activeEvent.group.id);
          if (groupResponse.success && groupResponse.data?.partnerTeams) {
            const teams = groupResponse.data.partnerTeams.sort((a, b) => {
              const aStartNumber = a.startNumber || 999;
              const bStartNumber = b.startNumber || 999;
              return aStartNumber - bStartNumber;
            });
            setPartnerTeams(teams);
            setParticipants([]);
          } else {
            setPartnerTeams([]);
            setParticipants([]);
          }
        } else {
          // Für normale Gruppen: Lade Teilnehmer
          const participantsResult = await participantsService.getAllParticipants();
          if (participantsResult.success) {
            const allParticipants = participantsResult.data || [];
            const eventParticipants = allParticipants.filter(p => {
              if (!p.groups || !Array.isArray(p.groups)) return false;
              return p.groups.some(g => g.id === activeEvent.group.id);
            });

            eventParticipants.sort((a, b) => {
              const aGroup = a.groups.find(g => g.id === activeEvent.group.id);
              const bGroup = b.groups.find(g => g.id === activeEvent.group.id);
              const aStartNumber = aGroup ? aGroup.startNumber : 999;
              const bStartNumber = bGroup ? bGroup.startNumber : 999;
              return aStartNumber - bStartNumber;
            });

            setParticipants(eventParticipants);
            setPartnerTeams([]);
          }
        }

        // Lade bestehende Ergebnisse (inkl. localStorage-Daten)
        if (activeEvent.group.id) {
          await loadResults(activeEvent.group.id);
        }
      } else {
        setParticipants([]);
        setPartnerTeams([]);
        setIsPartnerForm(false);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      addToast('error', 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "info" }), 5000);
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  // Hilfsfunktion: Zerlegt einen Score in seine Ziffern (z.B. 8.56 -> [8, 5, 6])
  // Gibt leere Strings zurück wenn Score 0 ist (außer für Penalty)
  const getScoreDigits = (score, isPenalty = false) => {
    const numScore = parseFloat(score) || 0;
    if (numScore === 0 && !isPenalty) {
      return ['', '', ''];
    }
    const str = numScore.toFixed(2);
    const parts = str.split('.');
    const digit0 = parseInt(parts[0]) || 0;
    const digit1 = parseInt(parts[1][0]) || 0;
    const digit2 = parseInt(parts[1][1]) || 0;
    return [digit0, digit1, digit2];
  };

  // Hilfsfunktion: Hole oder erstelle Input-Ref
  const getInputRef = (identifier, judgeIndexOrPenalty, digitIndex) => {
    const key = `${identifier}-${judgeIndexOrPenalty}-${digitIndex}`;
    if (!inputRefsMap.current.has(key)) {
      inputRefsMap.current.set(key, React.createRef());
    }
    return inputRefsMap.current.get(key);
  };

  // Handler für einzelne Ziffern-Änderung bei Judge-Scores
  const handleJudgeDigitChange = (identifier, judgeIndex, digitIndex, value, nextInputKey) => {
    setEditedData(prev => {
      const newMap = new Map(prev);
      const data = newMap.get(identifier) || {
        judgeScores: [0, 0, 0, 0, 0],
        judgeDigits: [[], [], [], [], []], // Speichere Ziffern separat: [['', '', ''], ...]
        penalty: 0
      };

      // Initialisiere judgeDigits falls nicht vorhanden
      if (!data.judgeDigits) {
        data.judgeDigits = data.judgeScores.map(score => {
          const scoreValue = parseFloat(score) || 0;
          return scoreValue === 0 ? ['', '', ''] : getScoreDigits(scoreValue, false);
        });
      }

      // Stelle sicher, dass alle Judge-Digits Arrays initialisiert sind
      for (let i = 0; i < 5; i++) {
        if (!data.judgeDigits[i] || !Array.isArray(data.judgeDigits[i])) {
          data.judgeDigits[i] = ['', '', ''];
        } else {
          // Stelle sicher, dass jedes Array genau 3 Elemente hat
          while (data.judgeDigits[i].length < 3) {
            data.judgeDigits[i].push('');
          }
          // Stelle sicher, dass undefined Werte durch leere Strings ersetzt werden
          data.judgeDigits[i] = data.judgeDigits[i].map(d => d === undefined || d === null ? '' : d);
        }
      }

      // Hole aktuelle Ziffern für diesen Judge
      const digits = [...(data.judgeDigits[judgeIndex] || ['', '', ''])];

      // Stelle sicher, dass digits genau 3 Elemente hat und keine undefined Werte enthält
      while (digits.length < 3) {
        digits.push('');
      }
      for (let i = 0; i < 3; i++) {
        if (digits[i] === undefined || digits[i] === null) {
          digits[i] = '';
        }
      }

      // Wenn Wert leer ist, setze entsprechendes Feld auf leer
      if (value === '') {
        digits[digitIndex] = '';
      } else {
        // Validiere Eingabe basierend auf Feld
        let newDigit = parseInt(value);
        if (isNaN(newDigit)) {
          // Ungültige Eingabe - speichere als String, damit Validierung greift
          digits[digitIndex] = value;
        } else {
          if (digitIndex === 0) {
            // Feld 1: 1-9
            if (newDigit < 1) newDigit = 1;
            if (newDigit > 9) newDigit = 9;
          } else {
            // Feld 2 und 3: 0-9
            if (newDigit < 0) newDigit = 0;
            if (newDigit > 9) newDigit = 9;
          }
          digits[digitIndex] = newDigit;
        }
      }

      // Aktualisiere judgeDigits - stelle sicher, dass alle Arrays initialisiert sind
      const updatedJudgeDigits = [];
      for (let i = 0; i < 5; i++) {
        if (i === judgeIndex) {
          // Für den aktuellen Judge verwende die aktualisierten digits
          updatedJudgeDigits[i] = [...digits];
        } else {
          // Für andere Judges verwende vorhandene Werte oder initialisiere mit leeren Strings
          const existingDigits = data.judgeDigits && data.judgeDigits[i] ? [...data.judgeDigits[i]] : ['', '', ''];
          // Stelle sicher, dass das Array genau 3 Elemente hat und keine undefined Werte
          while (existingDigits.length < 3) {
            existingDigits.push('');
          }
          updatedJudgeDigits[i] = existingDigits.map(d => d === undefined || d === null ? '' : d);
        }
      }

      // Erstelle Score aus Ziffern (verwende 0 für leere Felder nur für Berechnung)
      // Prüfe ob ein ungültiger Wert vorhanden ist (z.B. Buchstabe)
      const digit0 = digits[0] === '' ? 0 : (isNaN(parseInt(digits[0])) ? 0 : digits[0]);
      const digit1 = digits[1] === '' ? 0 : (isNaN(parseInt(digits[1])) ? 0 : digits[1]);
      const digit2 = digits[2] === '' ? 0 : (isNaN(parseInt(digits[2])) ? 0 : digits[2]);
      const newScore = parseFloat(`${digit0}.${digit1}${digit2}`);

      // Validierung: Maximal 10.00 - aber nicht automatisch auf 10 setzen, wenn > 10
      // Stattdessen den Score so lassen, damit die Validierung greifen kann
      const finalScore = isNaN(newScore) ? 0 : newScore;

      const updatedScores = [...data.judgeScores];
      updatedScores[judgeIndex] = finalScore;

      const averageScore = updatedScores.reduce((sum, s) => sum + s, 0) / updatedScores.length;
      const finalTotal = averageScore + (data.penalty || 0);

      newMap.set(identifier, {
        ...data,
        judgeScores: updatedScores,
        judgeDigits: updatedJudgeDigits,
        finalScore: finalTotal.toFixed(2)
      });

      return newMap;
    });

    // Auto-Focus zum nächsten Feld
    if (value && nextInputKey) {
      setTimeout(() => {
        const nextRef = inputRefsMap.current.get(nextInputKey);
        if (nextRef && nextRef.current) {
          nextRef.current.focus();
        }
      }, 10);
    }
  };

  // Handler für Pfeil-Klicks bei Judge-Scores
  const handleJudgeDigitArrow = (identifier, judgeIndex, digitIndex, direction) => {
    setEditedData(prev => {
      const newMap = new Map(prev);
      const data = newMap.get(identifier) || {
        judgeScores: [0, 0, 0, 0, 0],
        judgeDigits: [[], [], [], [], []],
        penalty: 0
      };

      // Initialisiere judgeDigits falls nicht vorhanden
      if (!data.judgeDigits) {
        data.judgeDigits = data.judgeScores.map(score => {
          const scoreValue = parseFloat(score) || 0;
          return scoreValue === 0 ? ['', '', ''] : getScoreDigits(scoreValue, false);
        });
      }

      const digits = data.judgeDigits[judgeIndex] || ['', '', ''];

      // Wenn Feld leer ist, starte mit 0 (für Feld 1) oder 0 (für Feld 2/3)
      let currentDigit = digits[digitIndex];
      if (currentDigit === '') {
        currentDigit = digitIndex === 0 ? 1 : 0;
      } else {
        currentDigit = parseInt(currentDigit) || 0;
      }

      const step = 1;
      let newDigit = currentDigit;

      if (direction === 'up') {
        newDigit += step;
      } else {
        newDigit -= step;
      }

      // Validierung
      if (digitIndex === 0) {
        if (newDigit < 1) newDigit = 1;
        if (newDigit > 9) newDigit = 9;
      } else {
        if (newDigit < 0) newDigit = 0;
        if (newDigit > 9) newDigit = 9;
      }

      digits[digitIndex] = newDigit;

      // Aktualisiere judgeDigits
      const updatedJudgeDigits = [...(data.judgeDigits || [])];
      updatedJudgeDigits[judgeIndex] = [...digits];

      // Erstelle Score aus Ziffern
      const digit0 = digits[0] === '' ? 0 : digits[0];
      const digit1 = digits[1] === '' ? 0 : digits[1];
      const digit2 = digits[2] === '' ? 0 : digits[2];
      const newScore = parseFloat(`${digit0}.${digit1}${digit2}`);
      const finalScore = newScore > 10 ? 10 : (isNaN(newScore) ? 0 : newScore);

      const updatedScores = [...data.judgeScores];
      updatedScores[judgeIndex] = finalScore;

      const averageScore = updatedScores.reduce((sum, s) => sum + s, 0) / updatedScores.length;
      const finalTotal = averageScore + (data.penalty || 0);

      newMap.set(identifier, {
        ...data,
        judgeScores: updatedScores,
        judgeDigits: updatedJudgeDigits,
        finalScore: finalTotal.toFixed(2)
      });

      return newMap;
    });
  };

  // Legacy Handler (für Kompatibilität)
  const handleScoreChange = (identifier, judgeIndex, newScore) => {
    let score = isNaN(newScore) ? 0 : parseFloat(newScore);
    if (score > 10) {
      addToast('error', 'Wertungsrichter-Punkte dürfen maximal 10.00 betragen');
      score = 10;
    }
    if (score < 0) score = 0;

    setEditedData(prev => {
      const newMap = new Map(prev);
      const data = newMap.get(identifier) || {
        judgeScores: [0, 0, 0, 0, 0],
        penalty: 0
      };

      const updatedScores = [...data.judgeScores];
      updatedScores[judgeIndex] = score;

      const averageScore = updatedScores.reduce((sum, s) => sum + s, 0) / updatedScores.length;
      const finalScore = averageScore + (data.penalty || 0);

      newMap.set(identifier, {
        ...data,
        judgeScores: updatedScores,
        finalScore: finalScore.toFixed(2)
      });

      return newMap;
    });
  };

  // Handler für Penalty-Ziffern-Änderung
  const handlePenaltyDigitChange = (identifier, digitIndex, value, nextInputKey) => {
    setEditedData(prev => {
      const newMap = new Map(prev);
      const data = newMap.get(identifier) || {
        judgeScores: [0, 0, 0, 0, 0],
        penalty: 0,
        penaltySign: '-'
      };

      // Validiere Eingabe: Alle Felder 0-9
      let newDigit = parseInt(value) || 0;
      if (newDigit < 0) newDigit = 0;
      if (newDigit > 9) newDigit = 9;

      // Hole aktuelle Ziffern direkt aus dem State, nicht aus dem berechneten Penalty
      // Das verhindert Race Conditions bei schneller Eingabe
      const currentPenalty = Math.abs(data.penalty || 0);
      let digits = getScoreDigits(currentPenalty, true);

      // Stelle sicher, dass digits ein Array mit 3 Elementen ist (alle Zahlen, keine Strings)
      if (!Array.isArray(digits) || digits.length !== 3) {
        digits = [0, 0, 0];
      }

      // Konvertiere alle Ziffern zu Zahlen (falls sie Strings sind)
      digits = digits.map(d => typeof d === 'string' && d === '' ? 0 : parseInt(d) || 0);

      // Setze die neue Ziffer an der richtigen Position
      digits[digitIndex] = newDigit;

      // Erstelle den neuen Penalty-Wert aus den Ziffern
      // Stelle sicher, dass wir immer eine gültige Zahl haben
      const newPenaltyValue = parseFloat(`${digits[0]}.${String(digits[1]).padStart(1, '0')}${String(digits[2]).padStart(1, '0')}`);
      const sign = data.penaltySign || '-';
      const finalPenalty = sign === '-' ? -newPenaltyValue : newPenaltyValue;

      const averageScore = data.judgeScores.reduce((sum, s) => sum + s, 0) / data.judgeScores.length;
      const finalScore = averageScore + finalPenalty;

      newMap.set(identifier, {
        ...data,
        penalty: finalPenalty,
        finalScore: finalScore.toFixed(2)
      });

      return newMap;
    });

    // Auto-Focus zum nächsten Feld und Text markieren
    if (value && nextInputKey) {
      setTimeout(() => {
        const nextRef = inputRefsMap.current.get(nextInputKey);
        if (nextRef && nextRef.current) {
          nextRef.current.focus();
          nextRef.current.select(); // Text markieren, damit er beim Tippen überschrieben wird
        }
      }, 10);
    }
  };

  // Handler für Penalty-Pfeil-Klicks
  const handlePenaltyDigitArrow = (identifier, digitIndex, direction) => {
    setEditedData(prev => {
      const newMap = new Map(prev);
      const data = newMap.get(identifier) || {
        judgeScores: [0, 0, 0, 0, 0],
        penalty: 0,
        penaltySign: '-'
      };

      const currentPenalty = Math.abs(data.penalty || 0);
      const digits = getScoreDigits(currentPenalty);

      let newDigit = digits[digitIndex];
      if (direction === 'up') {
        newDigit += 1;
      } else {
        newDigit -= 1;
      }

      if (newDigit < 0) newDigit = 0;
      if (newDigit > 9) newDigit = 9;

      digits[digitIndex] = newDigit;
      const newPenaltyValue = parseFloat(`${digits[0]}.${digits[1]}${digits[2]}`);
      const sign = data.penaltySign || '-';
      const finalPenalty = sign === '-' ? -newPenaltyValue : newPenaltyValue;

      const averageScore = data.judgeScores.reduce((sum, s) => sum + s, 0) / data.judgeScores.length;
      const finalScore = averageScore + finalPenalty;

      newMap.set(identifier, {
        ...data,
        penalty: finalPenalty,
        finalScore: finalScore.toFixed(2)
      });

      return newMap;
    });
  };

  // Handler für Penalty-Vorzeichen-Wechsel
  const handlePenaltySignChange = (identifier) => {
    setEditedData(prev => {
      const newMap = new Map(prev);
      const data = newMap.get(identifier) || {
        judgeScores: [0, 0, 0, 0, 0],
        penalty: 0,
        penaltySign: '-'
      };

      const currentSign = data.penaltySign || '-';
      const newSign = currentSign === '+' ? '-' : '+';
      const currentPenalty = Math.abs(data.penalty || 0);
      const finalPenalty = newSign === '-' ? -currentPenalty : currentPenalty;

      const averageScore = data.judgeScores.reduce((sum, s) => sum + s, 0) / data.judgeScores.length;
      const finalScore = averageScore + finalPenalty;

      newMap.set(identifier, {
        ...data,
        penalty: finalPenalty,
        penaltySign: newSign,
        finalScore: finalScore.toFixed(2)
      });

      return newMap;
    });
  };

  // Legacy Handler (für Kompatibilität)
  const handlePenaltyChange = (identifier, newPenalty) => {
    const penalty = isNaN(newPenalty) ? 0 : parseFloat(newPenalty);

    setEditedData(prev => {
      const newMap = new Map(prev);
      const data = newMap.get(identifier) || {
        judgeScores: [0, 0, 0, 0, 0],
        penalty: 0
      };

      const averageScore = data.judgeScores.reduce((sum, s) => sum + s, 0) / data.judgeScores.length;
      const finalScore = averageScore + penalty;

      newMap.set(identifier, {
        ...data,
        penalty,
        finalScore: finalScore.toFixed(2)
      });

      return newMap;
    });
  };

  // Prüfe ob alle Judge-Scores für einen Athleten valide sind
  const areJudgeScoresValid = (athleteData) => {
    if (!athleteData || !athleteData.judgeScores || !athleteData.judgeDigits) return false;

    // Hilfsfunktion: Prüfe ob eine Ziffer gültig ist
    const isDigitValid = (digit, min, max) => {
      if (digit === '' || digit === null || digit === undefined) return false; // Leer ist nicht OK für Validierung
      const digitStr = String(digit).trim();
      // Prüfe ob der String nur Zahlen enthält
      if (!/^\d+$/.test(digitStr)) {
        return false; // Enthält nicht-numerische Zeichen
      }
      const num = parseInt(digitStr, 10);
      if (isNaN(num)) return false; // Nicht-numerische Werte sind ungültig
      return num >= min && num <= max;
    };

    // Prüfe für jeden Judge-Score
    for (let i = 0; i < 5; i++) {
      const score = athleteData.judgeScores[i];
      const digits = athleteData.judgeDigits[i] || ['', '', ''];

      // Prüfe ob alle Ziffern ausgefüllt sind
      if (digits.some(d => d === '')) {
        return false;
      }

      // Prüfe ob alle Ziffern gültige Zahlen sind
      if (!isDigitValid(digits[0], 1, 9) || !isDigitValid(digits[1], 0, 9) || !isDigitValid(digits[2], 0, 9)) {
        return false; // Ungültige Zeichen gefunden
      }

      // Prüfe ob der Score im gültigen Bereich ist (0-10)
      const numScore = parseFloat(score);
      if (isNaN(numScore) || numScore < 0 || numScore > 10) {
        return false;
      }
    }

    return true;
  };

  // Prüfe ob ein einzelner Judge-Score valide ist
  const isJudgeScoreValid = (score, judgeDigits) => {
    if (!judgeDigits || judgeDigits.some(d => d === '')) {
      return false;
    }
    const numScore = parseFloat(score);
    return !isNaN(numScore) && numScore >= 0 && numScore <= 10;
  };

  // Prüfe ob ein spezifisches Ziffern-Feld invalide ist
  // Ein Feld ist invalide, wenn der daraus resultierende Score > 10 ist
  const isDigitFieldInvalid = (score, judgeDigits, digitIndex) => {
    if (!judgeDigits) return false;

    // Wenn das Feld leer ist, ist es nicht invalide (nur unvollständig)
    if (judgeDigits[digitIndex] === '') return false;

    // Prüfe ob der gesamte Score > 10 ist
    // Wenn ja, dann ist dieses Feld (und die anderen) invalide
    const numScore = parseFloat(score);
    if (!isNaN(numScore) && numScore > 10) {
      // Wenn Score > 10, markiere nur das Feld, das das Problem verursacht
      // Das ist normalerweise das Feld, das gerade bearbeitet wird oder das letzte ausgefüllte Feld
      // Für jetzt markieren wir alle Felder, wenn Score > 10
      // Aber wir könnten auch nur das letzte ausgefüllte Feld markieren
      return true;
    }

    return false;
  };

  // Prüfe ob alle Athleten (ohne DNS) Punkte haben
  const doAllAthletesHaveResults = () => {
    if (isPartnerForm) {
      const teamsWithoutDNS = partnerTeams.filter(team => {
        const identifier = `partner-team-${team.id}`;
        const status = participantStatuses.get(identifier) || {};
        return !status.didNotStart;
      });

      return teamsWithoutDNS.every(team => {
        const identifier = `partner-team-${team.id}`;
        return savedResults.has(identifier);
      });
    } else {
      const participantsWithoutDNS = participants.filter(participant => {
        const status = participantStatuses.get(participant.id) || {};
        return !status.didNotStart;
      });

      return participantsWithoutDNS.every(participant => {
        return savedResults.has(participant.id);
      });
    }
  };

  const handleSaveResult = async (identifier) => {
    if (!currentEvent || !currentEvent.group) return;

    const data = editedData.get(identifier);
    if (!data) return;

    // Validierung: Alle 5 Wertungsrichter-Punkte müssen ausgefüllt sein
    const hasAllJudgeScores = data.judgeScores.every(score =>
      score !== null && score !== undefined && score !== '' && !isNaN(parseFloat(score))
    );

    if (!hasAllJudgeScores) {
      addToast('error', 'Bitte alle 5 Wertungsrichter-Punkte eingeben');
      return;
    }

    // Prüfe ob alle Punkte > 0 sind
    const allScoresValid = data.judgeScores.every(score => {
      const numScore = parseFloat(score);
      return !isNaN(numScore) && numScore >= 0 && numScore <= 10;
    });

    if (!allScoresValid) {
      addToast('error', 'Wertungsrichter-Punkte müssen zwischen 0 und 10 liegen');
      return;
    }

    try {
      setSavingParticipants(prev => new Set(prev).add(identifier));

      const groupId = currentEvent.group.id;
      const resultId = savedResults.get(identifier);

      // Prüfe ob es ein Partner Team ist
      const identifierStr = String(identifier);
      const isPartnerTeam = identifierStr.startsWith('partner-team-');
      const partnerTeamId = isPartnerTeam ? parseInt(identifierStr.replace('partner-team-', '')) : null;
      const participantId = isPartnerTeam ? null : (typeof identifier === 'number' ? identifier : parseInt(identifierStr));

      const resultData = {
        groupId,
        judgeScore1: parseFloat(data.judgeScores[0]) || 0,
        judgeScore2: parseFloat(data.judgeScores[1]) || 0,
        judgeScore3: parseFloat(data.judgeScores[2]) || 0,
        judgeScore4: parseFloat(data.judgeScores[3]) || 0,
        judgeScore5: parseFloat(data.judgeScores[4]) || 0,
        penalty: parseFloat(data.penalty) || 0,
        status: 'Vorläufig'
      };

      if (partnerTeamId) {
        resultData.partnerTeamId = partnerTeamId;
        resultData.participantId = null;
      } else {
        resultData.participantId = participantId;
        resultData.partnerTeamId = null;
      }

      let result;
      if (resultId) {
        // Update bestehendes Ergebnis
        result = await resultsService.updateResult(resultId, resultData);
      } else {
        // Erstelle neues Ergebnis
        result = await resultsService.createResult(resultData);
        if (result.success && result.data?.id) {
          setSavedResults(prev => new Map(prev).set(identifier, result.data.id));
        }
      }

      if (!result || !result.success) {
        const errorMessage = result?.message || result?.error || 'Unbekannter Fehler beim Speichern';
        addToast('error', `Fehler beim Speichern: ${errorMessage}`);
        return;
      }

      addToast('success', 'Ergebnis erfolgreich gespeichert!');

      // Entferne gespeicherte Daten für diesen Identifier aus localStorage
      // (da sie jetzt auf dem Server gespeichert sind)
      setEditedData(prev => {
        const newMap = new Map(prev);
        newMap.delete(identifier);
        return newMap;
      });

      // Lade Ergebnisse neu, um Ränge zu aktualisieren
      await loadResults(groupId);

      // Setze manuelle Auswahl zurück und aktualisiere die angezeigten Athleten direkt
      updateManualSelection(false);
      setCurrentAthletes(getCurrentAthletesForInput);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      addToast('error', `Fehler beim Speichern: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setSavingParticipants(prev => {
        const newSet = new Set(prev);
        newSet.delete(identifier);
        return newSet;
      });
    }
  };

  const loadResults = async (groupId) => {
    if (!groupId) return;

    try {
      const resultsResult = await resultsService.getResultsByGroup(groupId);
      if (resultsResult.success && resultsResult.data && Array.isArray(resultsResult.data)) {
        const existingResults = resultsResult.data;
        const savedResultsMap = new Map();

        // Erstelle Map mit gespeicherten Ergebnissen
        const savedResultsData = new Map();
        const statusesMap = new Map();

        existingResults.forEach(result => {
          // Identifiziere ob es ein Teilnehmer, Nation oder Partner Team ist
          const identifier = result.partnerTeamId
            ? `partner-team-${result.partnerTeamId}`
            : result.participantId
              ? result.participantId
              : (result.nationId ? `nation-${result.nationId}` : null);

          if (identifier) {
            // Prüfe ob es ein tatsächliches Ergebnis ist (mit Scores) oder nur ein Platzhalter (DNS/Nachzügler ohne Results)
            const hasActualScores = result.judgeScore1 > 0 || result.judgeScore2 > 0 || result.judgeScore3 > 0 ||
              result.judgeScore4 > 0 || result.judgeScore5 > 0;

            if (hasActualScores) {
              const judgeScores = [
                parseFloat(result.judgeScore1) || 0,
                parseFloat(result.judgeScore2) || 0,
                parseFloat(result.judgeScore3) || 0,
                parseFloat(result.judgeScore4) || 0,
                parseFloat(result.judgeScore5) || 0
              ];
              // Initialisiere judgeDigits aus den geladenen Scores
              const judgeDigits = judgeScores.map(score => {
                const scoreValue = parseFloat(score) || 0;
                return scoreValue === 0 ? ['', '', ''] : getScoreDigits(scoreValue, false);
              });
              const penalty = parseFloat(result.penalty) || 0;
              const penaltySign = penalty > 0 ? '+' : '-';
              const averageScore = judgeScores.reduce((sum, s) => sum + s, 0) / judgeScores.length;
              const finalScore = averageScore + penalty;

              savedResultsData.set(identifier, {
                judgeScores,
                judgeDigits,
                penalty,
                penaltySign,
                finalScore: finalScore.toFixed(2)
              });

              if (result.id) {
                savedResultsMap.set(identifier, result.id);
              }
            }

            // Speichere DNS- und Nachzügler-Status
            statusesMap.set(identifier, {
              didNotStart: result.didNotStart || false,
              isLate: result.isLate || false,
              checkedByCaller: result.checkedByCaller || false
            });
          }
        });

        setParticipantStatuses(statusesMap);

        // Lade gespeicherte eingegebene Daten aus localStorage
        const storedEditedData = loadEditedDataFromStorage(groupId);

        // Merge: Behalte nicht gespeicherte Änderungen, aktualisiere nur gespeicherte Ergebnisse
        setEditedData(prev => {
          const newMap = new Map(prev);

          // Aktualisiere gespeicherte Ergebnisse mit den neuesten Daten vom Server
          savedResultsData.forEach((data, identifier) => {
            // Nur aktualisieren, wenn das Ergebnis bereits gespeichert war (um nicht gespeicherte Änderungen nicht zu überschreiben)
            if (savedResultsMap.has(identifier)) {
              newMap.set(identifier, data);
            } else {
              // Wenn noch nicht gespeichert, aber Daten vom Server vorhanden sind, diese verwenden
              // (z.B. wenn jemand anders das Ergebnis gespeichert hat)
              if (!newMap.has(identifier)) {
                newMap.set(identifier, data);
              }
              // Wenn bereits lokale Änderungen vorhanden sind, diese behalten
            }
          });

          // Füge gespeicherte eingegebene Daten aus localStorage hinzu (nur wenn noch nicht vorhanden)
          storedEditedData.forEach((value, key) => {
            // Nur hinzufügen, wenn noch nicht vorhanden (Server-Daten und aktuelle Änderungen haben Priorität)
            if (!newMap.has(key)) {
              newMap.set(key, value);
            }
          });

          return newMap;
        });

        setSavedResults(savedResultsMap);
      }
    } catch (error) {
      console.warn('Fehler beim Laden der Ergebnisse:', error);
    }
  };

  const handleCompleteGroup = async () => {
    if (!currentEvent || !currentEvent.group) return;

    if (isPartnerForm) {
      // Für Partnerform: Prüfe ob alle Teams (ohne DNS) Ergebnisse haben
      const teamsWithoutDNS = partnerTeams.filter(team => {
        const identifier = `partner-team-${team.id}`;
        const status = participantStatuses.get(identifier) || {};
        return !status.didNotStart; // Ignoriere DNS-Teams
      });

      const allTeamsHaveResults = teamsWithoutDNS.every(team => {
        const identifier = `partner-team-${team.id}`;
        return savedResults.has(identifier);
      });

      if (!allTeamsHaveResults) {
        addToast('error', 'Bitte für alle Partner Teams (außer DNS) Ergebnisse speichern, bevor die Gruppe aktualisiert wird');
        return;
      }
    } else {
      // Für normale Gruppen: Prüfe ob alle Teilnehmer (ohne DNS) Ergebnisse haben
      const participantsWithoutDNS = participants.filter(participant => {
        const status = participantStatuses.get(participant.id) || {};
        return !status.didNotStart; // Ignoriere DNS-Teilnehmer
      });

      const allParticipantsHaveResults = participantsWithoutDNS.every(participant => {
        return savedResults.has(participant.id);
      });

      if (!allParticipantsHaveResults) {
        addToast('error', 'Bitte für alle Teilnehmer (außer DNS) Ergebnisse speichern, bevor die Gruppe aktualisiert wird');
        return;
      }
    }

    try {
      setIsSaving(true);
      const groupId = currentEvent.group.id;

      if (isAdminEdit) {
        // Admin-Bearbeitung: Aktualisiere die Gruppe (ohne sie abzuschließen)
        // Sammle alle gespeicherten Ergebnisse
        const resultsToUpdate = [];

        if (isPartnerForm) {
          partnerTeams.forEach(team => {
            const identifier = `partner-team-${team.id}`;
            const status = participantStatuses.get(identifier) || {};
            if (!status.didNotStart && savedResults.has(identifier)) {
              const athleteData = editedData.get(identifier);
              if (athleteData) {
                resultsToUpdate.push({
                  partnerTeamId: team.id,
                  judgeScores: athleteData.judgeScores || [0, 0, 0, 0, 0],
                  penalty: athleteData.penalty || 0
                });
              }
            }
          });
        } else {
          participants.forEach(participant => {
            const status = participantStatuses.get(participant.id) || {};
            if (!status.didNotStart && savedResults.has(participant.id)) {
              const athleteData = editedData.get(participant.id);
              if (athleteData) {
                resultsToUpdate.push({
                  participantId: participant.id,
                  judgeScores: athleteData.judgeScores || [0, 0, 0, 0, 0],
                  penalty: athleteData.penalty || 0
                });
              }
            }
          });
        }

        // Aktualisiere die Ergebnisse
        const result = await resultsService.updateGroupResults(groupId, resultsToUpdate);

        if (!result || !result.success) {
          const errorMessage = result?.message || result?.error || 'Unbekannter Fehler beim Aktualisieren';
          addToast('error', `Fehler beim Aktualisieren: ${errorMessage}`);
          return;
        }

        addToast('success', 'Gruppe erfolgreich aktualisiert!');

        // Navigiere zurück zur Results-Seite
        navigate('/admin/results');
      } else {
        // Normale Scorekeeper-Ansicht: Schließe die Gruppe ab
        const headers = {
          'Content-Type': 'application/json'
        };

        if (typeof window !== 'undefined') {
          const token = localStorage.getItem('authToken');
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
        }

        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${baseURL}/results/group/${groupId}/complete`, {
          method: 'POST',
          headers
        });

        const responseData = await response.json();

        if (!response.ok || !responseData.success) {
          const errorMessage = responseData.message || responseData.error || 'Unbekannter Fehler beim Abschließen';
          addToast('error', `Fehler beim Abschließen: ${errorMessage}`);
          return;
        }

        addToast('success', 'Gruppe erfolgreich abgeschlossen! Das nächste Event wird gestartet.');

        // Lösche gespeicherte eingegebene Daten aus localStorage
        clearEditedDataFromStorage(groupId);

        // Lade Daten neu, um das neue Event zu sehen
        await loadData();
      }
    } catch (error) {
      console.error('Fehler:', error);
      addToast('error', `Fehler: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Berechne alle nicht-DNS Teilnehmer in sortierter Reihenfolge (für Run-Berechnung)
  // MUSS vor allen bedingten Returns stehen (React Hooks Regel)
  const allEligibleAthletes = useMemo(() => {
    if (!currentEvent || !currentEvent.group) return [];

    if (isPartnerForm) {
      const sortedTeams = [...partnerTeams].sort((a, b) => {
        const aStartNumber = a.startNumber || 999;
        const bStartNumber = b.startNumber || 999;
        return aStartNumber - bStartNumber;
      });
      return sortedTeams.filter(team => {
        const identifier = `partner-team-${team.id}`;
        const status = participantStatuses.get(identifier) || {};
        return !status.didNotStart;
      });
    } else {
      const sortedParticipants = [...participants].sort((a, b) => {
        const aGroup = a.groups?.find(g => g.id === currentEvent.group.id);
        const bGroup = b.groups?.find(g => g.id === currentEvent.group.id);
        const aStartNumber = aGroup ? aGroup.startNumber : 999;
        const bStartNumber = bGroup ? bGroup.startNumber : 999;
        return aStartNumber - bStartNumber;
      });

      // Zuerst alle regulären Teilnehmer (nicht DNS, nicht Nachzügler)
      const nonLate = sortedParticipants.filter(participant => {
        const status = participantStatuses.get(participant.id) || {};
        return !status.didNotStart && !status.isLate;
      });

      // Dann alle Nachzügler (nicht DNS, aber isLate)
      const late = sortedParticipants.filter(participant => {
        const status = participantStatuses.get(participant.id) || {};
        return !status.didNotStart && status.isLate;
      });

      return [...nonLate, ...late];
    }
  }, [currentEvent, participants, partnerTeams, isPartnerForm, participantStatuses]);

  // Berechne die Gesamtzahl der Runs und den aktuellen Run-Fortschritt
  const runInfo = useMemo(() => {
    if (!currentEvent || !currentEvent.group) return { totalRuns: 0, currentRun: 0 };
    const isTwoParallel = currentEvent.group.twoParallel || currentEvent.group.two_parallel || false;
    if (!isTwoParallel) return { totalRuns: 0, currentRun: 0 };
    const totalRuns = Math.ceil(allEligibleAthletes.length / 2);
    return { totalRuns, currentRun: currentRunIndex + 1 };
  }, [currentEvent, allEligibleAthletes, currentRunIndex]);

  // Finde die nächsten Athleten ohne Ergebnisse für die Eingabekarten
  // MUSS vor allen bedingten Returns stehen (React Hooks Regel)
  const getCurrentAthletesForInput = useMemo(() => {
    if (!currentEvent || !currentEvent.group) return [null, null];

    const isTwoParallel = currentEvent.group.twoParallel || currentEvent.group.two_parallel || false;

    if (isTwoParallel) {
      // Run-basierte Logik: Athleten werden in Runs (Paare) gruppiert
      // Der currentRunIndex bestimmt, welches Paar angezeigt wird
      const runStart = currentRunIndex * 2;
      const athlete0 = allEligibleAthletes[runStart] || null;
      const athlete1 = allEligibleAthletes[runStart + 1] || null;

      // Prüfe ob ein Athlet im aktuellen Run bereits gespeichert wurde
      // Wenn ja, setze ihn auf null (Karte schließt sich)
      const id0 = athlete0 ? (isPartnerForm ? `partner-team-${athlete0.id}` : athlete0.id) : null;
      const id1 = athlete1 ? (isPartnerForm ? `partner-team-${athlete1.id}` : athlete1.id) : null;

      const result0 = (athlete0 && id0 && savedResults.has(id0)) ? null : athlete0;
      const result1 = (athlete1 && id1 && savedResults.has(id1)) ? null : athlete1;

      return [result0, result1];
    } else {
      // Einzelmodus: Nächsten ungespeicherten Athleten anzeigen
      const next = allEligibleAthletes.find(a => {
        const id = isPartnerForm ? `partner-team-${a.id}` : a.id;
        return !savedResults.has(id);
      });
      return [next || null, null];
    }
  }, [currentEvent, allEligibleAthletes, isPartnerForm, savedResults, currentRunIndex]);

  // Automatisches Weiterspringen zum nächsten Run, wenn beide Athleten des aktuellen Runs gespeichert sind
  useEffect(() => {
    if (!currentEvent || !currentEvent.group) return;
    const isTwoParallel = currentEvent.group.twoParallel || currentEvent.group.two_parallel || false;
    if (!isTwoParallel) return;

    const runStart = currentRunIndex * 2;
    const athlete0 = allEligibleAthletes[runStart] || null;
    const athlete1 = allEligibleAthletes[runStart + 1] || null;

    // Prüfe ob beide Athleten des Runs gespeichert sind (oder nicht existieren)
    const id0 = athlete0 ? (isPartnerForm ? `partner-team-${athlete0.id}` : athlete0.id) : null;
    const id1 = athlete1 ? (isPartnerForm ? `partner-team-${athlete1.id}` : athlete1.id) : null;

    const athlete0Done = !athlete0 || (id0 && savedResults.has(id0));
    const athlete1Done = !athlete1 || (id1 && savedResults.has(id1));

    if (athlete0Done && athlete1Done && athlete0) {
      // Beide gespeichert - springe zum nächsten Run (falls es noch einen gibt)
      const totalRuns = Math.ceil(allEligibleAthletes.length / 2);
      if (currentRunIndex < totalRuns - 1) {
        setCurrentRunIndex(prev => prev + 1);
      }
    }
  }, [currentEvent, allEligibleAthletes, isPartnerForm, savedResults, currentRunIndex]);

  // Aktualisiere currentAthletes wenn sich getCurrentAthletesForInput ändert
  // MUSS vor allen bedingten Returns stehen (React Hooks Regel)
  // Überspringe automatische Aktualisierung, wenn manuelle Auswahl aktiv ist
  // Nutzt Ref statt State für die Prüfung, um Race Conditions zu vermeiden
  useEffect(() => {
    if (!isManualSelectionRef.current) {
      setCurrentAthletes(getCurrentAthletesForInput);
    }
  }, [getCurrentAthletesForInput]);

  // Keyboard-Shortcut: STRG+S zum Speichern
  useEffect(() => {
    const handleKeyDown = (event) => {
      // STRG+S oder CMD+S (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();

        // Prüfe ob aktive Athleten vorhanden sind
        if (!currentAthletes || currentAthletes.length === 0) return;

        // Speichere alle aktiven Athleten, die valide sind
        currentAthletes.forEach((athlete) => {
          if (!athlete) return;

          const identifier = isPartnerForm
            ? `partner-team-${athlete.id}`
            : athlete.id;

          const athleteData = editedData.get(identifier);
          if (!athleteData) return;

          const status = participantStatuses.get(identifier) || {};
          const didNotStart = status.didNotStart || false;

          // Prüfe ob der Button nicht deaktiviert wäre
          if (!didNotStart && !savingParticipants.has(identifier) && areJudgeScoresValid(athleteData)) {
            handleSaveResult(identifier);
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentAthletes, editedData, participantStatuses, savingParticipants, isPartnerForm, areJudgeScoresValid, handleSaveResult]);

  const handleSwapAthletes = () => {
    updateManualSelection(true);
    setCurrentAthletes([currentAthletes[1], currentAthletes[0]]);
  };

  // Wenn kein Venue zugewiesen ist, zeige Flächenauswahl
  // Bei Admin-Bearbeitung: Überspringe Venue-Auswahl
  // Wenn kein Venue zugewiesen ist und keine Admin-Bearbeitung, zeige Flächenauswahl
  if (!isAdminEdit && !assignedVenueFromUser && !selectedVenue) {
    return (
      <TailwindPageWrapper>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors pt-24">
          <div className="max-w-md mx-auto px-4 py-8">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("scorekeeper.selectVenue.title", "Fläche auswählen")}
                </h1>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t("scorekeeper.selectVenue.subtitle", "Wähle die Fläche aus, für die du Ergebnisse erfassen möchtest.")}
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              {[
                { value: "Fläche A", label: "Fläche A", icon: "fa-map-marker-alt", sub: t("scorekeeper.selectVenue.venueA", "Ergebnisse für Fläche A erfassen") },
                { value: "Fläche B", label: "Fläche B", icon: "fa-map-marker-alt", sub: t("scorekeeper.selectVenue.venueB", "Ergebnisse für Fläche B erfassen") },
              ].map((venue, i, arr) => (
                <button
                  key={venue.value}
                  onClick={() => setSelectedVenue(venue.value)}
                  className={`w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-primary/5 dark:hover:bg-primary-light/5 transition-colors group ${i < arr.length - 1 ? 'border-b border-slate-100 dark:border-slate-700/60' : ''}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 dark:group-hover:bg-primary-light/15 transition-colors">
                    <i className={`fas ${venue.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{venue.label}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{venue.sub}</div>
                  </div>
                  <i className="fas fa-chevron-right text-xs text-slate-400 dark:text-slate-500 group-hover:text-primary dark:group-hover:text-primary-light transition-colors flex-shrink-0"></i>
                </button>
              ))}
            </div>
          </div>
        </div>
      </TailwindPageWrapper>
    );
  }

  if (loading) {
    return (
      <TailwindPageWrapper>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-7 bg-slate-200 dark:bg-slate-700 rounded-lg w-56"></div>
              <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
            </div>
            <div className="grid grid-cols-1 gap-6 mb-8">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="h-12 bg-slate-200 dark:bg-slate-700"></div>
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-5 gap-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i}>
                        <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded w-4 mb-2 mx-auto"></div>
                        <div className="h-14 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-100 dark:border-slate-700 pt-4 flex gap-4">
                    <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl w-32"></div>
                    <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl flex-1"></div>
                    <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl w-28"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="h-12 bg-slate-200 dark:bg-slate-700"></div>
              <div className="p-4 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 py-2">
                    <div className="w-7 h-7 bg-slate-200 dark:bg-slate-700 rounded-lg flex-shrink-0"></div>
                    <div className="flex-1 h-4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                    <div className="h-5 w-20 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </TailwindPageWrapper>
    );
  }

  return (
    <TailwindPageWrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {currentEvent ? (
          <>
            <div className="mb-5">
                <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2.5 mb-0.5">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      {currentEvent.group?.twoParallel || currentEvent.group?.two_parallel
                        ? "Parallel-Modus"
                        : "Punkteerfassung"}
                    </h1>
                    {(currentEvent.group?.twoParallel || currentEvent.group?.two_parallel) && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20">
                        <i className="fas fa-columns text-[10px]"></i>
                        2 Bahnen
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {[
                      currentEvent.group?.category?.name,
                      currentEvent.group?.ageGroup?.name,
                      currentEvent.group?.gender,
                      currentEvent.group?.id ? `Gruppe ${currentEvent.group.id}` : null
                    ].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!assignedVenueFromUser && (
                    <select
                      value={selectedVenue || ""}
                      onChange={(e) => setSelectedVenue(e.target.value)}
                      className="h-9 px-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
                    >
                      <option value="">Fläche wählen…</option>
                      <option value="Fläche A">Fläche A</option>
                      <option value="Fläche B">Fläche B</option>
                    </select>
                  )}
                  <button
                    onClick={() => {
                      const venue = assignedVenue || selectedVenue || 'Fläche A';
                      window.open(`/live-results?venue=${encodeURIComponent(venue)}`, '_blank');
                    }}
                    className="inline-flex items-center gap-1.5 h-9 px-4 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-200 dark:border-slate-600 hover:border-primary/50 dark:hover:border-primary-light/50 hover:text-primary dark:hover:text-primary-light transition-colors"
                  >
                    <i className="fas fa-tv text-xs"></i>
                    Live-View
                  </button>
                  {(currentEvent.group?.twoParallel || currentEvent.group?.two_parallel) && currentAthletes[0] && currentAthletes[1] && (
                    <button
                      onClick={handleSwapAthletes}
                      className="inline-flex items-center gap-1.5 h-9 px-4 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-200 dark:border-slate-600 hover:border-primary/50 dark:hover:border-primary-light/50 hover:text-primary dark:hover:text-primary-light transition-colors"
                    >
                      <i className="fas fa-exchange-alt text-xs"></i>
                      Tauschen
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Run-Indikator für Parallel-Modus */}
            {(currentEvent.group?.twoParallel || currentEvent.group?.two_parallel) && runInfo.totalRuns > 0 && (
              <div className="mb-4 flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 h-8 px-3.5 bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light text-xs font-semibold rounded-xl border border-primary/20 dark:border-primary-light/20">
                  <i className="fas fa-running text-[10px]"></i>
                  Run {runInfo.currentRun} / {runInfo.totalRuns}
                </span>
                {runInfo.currentRun < runInfo.totalRuns && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    Beide Athleten speichern → nächster Run
                  </span>
                )}
              </div>
            )}

            {/* Große Eingabekarten für aktuelle Athleten */}
            {(() => {
              const isTwoParallel = currentEvent.group?.twoParallel || currentEvent.group?.two_parallel || false;
              const athletesToShow = isTwoParallel ? currentAthletes : [currentAthletes[0]];

              return (
                <div ref={athleteCardsRef} className={`grid gap-6 mb-8 ${isTwoParallel ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                  {athletesToShow.map((athlete, index) => {
                    if (!athlete) return null;

                    const identifier = isPartnerForm
                      ? `partner-team-${athlete.id}`
                      : athlete.id;
                    const athleteData = editedData.get(identifier) || {
                      judgeScores: [0, 0, 0, 0, 0],
                      penalty: 0,
                      penaltySign: '-',
                      finalScore: 0
                    };
                    const status = participantStatuses.get(identifier) || {};
                    const didNotStart = status.didNotStart || false;

                    const colors = index === 0
                      ? { bg: 'bg-primary', text: 'text-white', border: 'border-primary-dark', button: 'bg-primary hover:bg-primary-dark' }
                      : { bg: 'bg-violet-500', text: 'text-violet-50', border: 'border-violet-600', button: 'bg-violet-600 hover:bg-violet-700' };

                    const athleteLabel = index === 0 ? 'ATHLET BAHN A' : 'ATHLET BAHN B';
                    const nationCode = isPartnerForm
                      ? (athlete.participant1?.nation?.code || 'DE')
                      : (athlete.nation?.code || athlete.nationCode || 'DE');
                    const athleteName = isPartnerForm
                      ? `${athlete.participant1?.name || 'Unbekannt'} & ${athlete.participant2?.name || 'Unbekannt'}`
                      : athlete.name;
                    const nationName = isPartnerForm
                      ? (athlete.participant1?.nation?.name || 'Nation')
                      : (athlete.nation?.name || athlete.nation || 'Nation');
                    const startNumber = isPartnerForm
                      ? (athlete.startNumber || 999)
                      : (athlete.groups?.find(g => g.id === currentEvent.group.id)?.startNumber || 999);

                    return (
                      <div key={identifier} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className={`${colors.bg} ${colors.text} px-5 py-2.5 flex items-center justify-between`}>
                          <span className="font-semibold text-xs tracking-widest uppercase">{athleteLabel}</span>
                          <span className="text-xs font-semibold opacity-90 tabular-nums">
                            <i className="fas fa-hashtag text-[9px] mr-0.5 opacity-70"></i>
                            {String(startNumber).padStart(2, '0')}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          {/* Name und Nation */}
                          <div className="flex items-center gap-3 mb-5">
                            <img
                              src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                              alt={nationName}
                              className="w-9 h-9 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">
                                {athleteName}
                              </div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                {nationName} · {nationCode}
                              </div>
                            </div>
                          </div>

                          {/* Wertungsrichter-Punkte */}
                          <div className="flex items-start mb-4">
                            {athleteData.judgeScores.map((score, judgeIndex) => {
                              // Verwende gespeicherte Ziffern falls vorhanden, sonst berechne aus Score
                              const digits = athleteData.judgeDigits && athleteData.judgeDigits[judgeIndex]
                                ? athleteData.judgeDigits[judgeIndex]
                                : (() => {
                                  const scoreValue = (score !== null && score !== undefined && !isNaN(parseFloat(score))) ? parseFloat(score) : 0;
                                  return getScoreDigits(scoreValue, false);
                                })();
                              const refKey1 = `${identifier}-${judgeIndex}-0`;
                              const refKey2 = `${identifier}-${judgeIndex}-1`;
                              const refKey3 = `${identifier}-${judgeIndex}-2`;
                              const ref1 = getInputRef(identifier, judgeIndex, 0);
                              const ref2 = getInputRef(identifier, judgeIndex, 1);
                              const ref3 = getInputRef(identifier, judgeIndex, 2);

                              // Prüfe ob einzelne Felder ungültige Werte haben (z.B. Buchstaben)
                              const isDigitValid = (digit, min, max) => {
                                if (digit === '' || digit === null || digit === undefined) return true; // Leer ist OK
                                // Konvertiere zu String und prüfe ob es nur Zahlen enthält
                                const digitStr = String(digit).trim();
                                // Prüfe ob der String nur Zahlen enthält
                                if (!/^\d+$/.test(digitStr)) {
                                  return false; // Enthält nicht-numerische Zeichen
                                }
                                const num = parseInt(digitStr, 10);
                                if (isNaN(num)) return false; // Nicht-numerische Werte sind ungültig
                                return num >= min && num <= max;
                              };

                              // Prüfe jedes Feld einzeln auf ungültige Werte
                              const field0HasInvalidValue = digits[0] !== '' && !isDigitValid(digits[0], 1, 9);
                              const field1HasInvalidValue = digits[1] !== '' && !isDigitValid(digits[1], 0, 9);
                              const field2HasInvalidValue = digits[2] !== '' && !isDigitValid(digits[2], 0, 9);

                              // Prüfe ob der Score invalide ist (> 10)
                              const numScore = parseFloat(score);
                              const scoreInvalid = !isNaN(numScore) && numScore > 10;

                              // Bestimme welche Felder invalide sind
                              let field0Invalid = field0HasInvalidValue;
                              let field1Invalid = field1HasInvalidValue;
                              let field2Invalid = field2HasInvalidValue;

                              // Wenn Score > 10, markiere nur das Feld, das das Problem verursacht
                              if (scoreInvalid && !field0HasInvalidValue && !field1HasInvalidValue && !field2HasInvalidValue) {
                                // Wenn alle Felder gültige Werte haben, aber Score > 10
                                // Markiere das Feld, das den Score über 10 bringt
                                if (digits[0] !== '' && digits[1] !== '' && digits[2] !== '') {
                                  if (digits[0] === '1' && parseInt(digits[1]) > 0) {
                                    field1Invalid = true;
                                  } else if (digits[0] === '1' && parseInt(digits[2]) > 0 && parseInt(digits[1]) === 0) {
                                    field2Invalid = true;
                                  } else if (parseInt(digits[0]) > 1) {
                                    field0Invalid = true;
                                  } else {
                                    field1Invalid = true;
                                  }
                                } else {
                                  // Wenn nicht alle Felder ausgefüllt sind, markiere das letzte ausgefüllte Feld
                                  if (digits[2] !== '') field2Invalid = true;
                                  else if (digits[1] !== '') field1Invalid = true;
                                  else if (digits[0] !== '') field0Invalid = true;
                                }
                              }

                              return (
                                <React.Fragment key={judgeIndex}>
                                  {judgeIndex > 0 && (
                                    <div className="w-px self-stretch bg-slate-200 dark:bg-slate-600 mx-3 flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5 text-center">
                                    J{judgeIndex + 1}
                                  </label>
                                  <div className="flex items-center justify-center gap-1">
                                    {/* Feld 1 (Einer) */}
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      pattern="[1-9]"
                                      maxLength="1"
                                      value={digits[0] === '' ? '' : String(digits[0])}
                                      onChange={(e) => {
                                        handleJudgeDigitChange(identifier, judgeIndex, 0, e.target.value, refKey2);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'ArrowRight') {
                                          setTimeout(() => { ref2.current?.focus(); ref2.current?.select(); }, 10);
                                        } else if (e.key === 'ArrowLeft') {
                                          if (judgeIndex > 0) {
                                            const prevRef3 = getInputRef(identifier, judgeIndex - 1, 2);
                                            setTimeout(() => { prevRef3.current?.focus(); prevRef3.current?.select(); }, 10);
                                          }
                                        } else if (e.key === 'Backspace' && e.currentTarget.value === '') {
                                          e.preventDefault();
                                          if (judgeIndex > 0) {
                                            const prevRef3 = getInputRef(identifier, judgeIndex - 1, 2);
                                            setTimeout(() => { prevRef3.current?.focus(); prevRef3.current?.select(); }, 10);
                                          }
                                        }
                                      }}
                                      disabled={didNotStart}
                                      ref={ref1}
                                      className={`w-7 px-0.5 py-1.5 text-base font-semibold text-center border rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${didNotStart
                                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-300 dark:border-slate-600'
                                        : field0Invalid
                                          ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-500 dark:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                                          : 'bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light'
                                        }`}
                                    />

                                    {/* Punkt */}
                                    <span className="text-base font-bold text-slate-700 dark:text-slate-300">.</span>

                                    {/* Feld 2 (Zehntel) */}
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      pattern="[0-9]"
                                      maxLength="1"
                                      value={digits[1] === '' ? '' : String(digits[1])}
                                      onChange={(e) => {
                                        handleJudgeDigitChange(identifier, judgeIndex, 1, e.target.value, refKey3);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'ArrowRight') {
                                          setTimeout(() => { ref3.current?.focus(); ref3.current?.select(); }, 10);
                                        } else if (e.key === 'ArrowLeft') {
                                          setTimeout(() => { ref1.current?.focus(); ref1.current?.select(); }, 10);
                                        } else if (e.key === 'Backspace' && e.currentTarget.value === '') {
                                          e.preventDefault();
                                          setTimeout(() => { ref1.current?.focus(); ref1.current?.select(); }, 10);
                                        }
                                      }}
                                      disabled={didNotStart}
                                      ref={ref2}
                                      className={`w-7 px-0.5 py-1.5 text-base font-semibold text-center border rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${didNotStart
                                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-300 dark:border-slate-600'
                                        : field1Invalid
                                          ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-500 dark:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                                          : 'bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light'
                                        }`}
                                    />

                                    {/* Feld 3 (Hundertstel) */}
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      pattern="[0-9]"
                                      maxLength="1"
                                      value={digits[2] === '' ? '' : String(digits[2])}
                                      onChange={(e) => {
                                        handleJudgeDigitChange(identifier, judgeIndex, 2, e.target.value, null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'ArrowRight') {
                                          if (judgeIndex < 4) {
                                            const nextRef1 = getInputRef(identifier, judgeIndex + 1, 0);
                                            setTimeout(() => { nextRef1.current?.focus(); nextRef1.current?.select(); }, 10);
                                          }
                                        } else if (e.key === 'ArrowLeft') {
                                          setTimeout(() => { ref2.current?.focus(); ref2.current?.select(); }, 10);
                                        } else if (e.key === 'Backspace' && e.currentTarget.value === '') {
                                          e.preventDefault();
                                          setTimeout(() => { ref2.current?.focus(); ref2.current?.select(); }, 10);
                                        }
                                      }}
                                      disabled={didNotStart}
                                      ref={ref3}
                                      className={`w-7 px-0.5 py-1.5 text-base font-semibold text-center border rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${didNotStart
                                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-300 dark:border-slate-600'
                                        : field2Invalid
                                          ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-500 dark:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                                          : 'bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light'
                                        }`}
                                    />
                                  </div>
                                </div>
                                </React.Fragment>
                              );
                            })}
                          </div>

                          {/* Trennlinie */}
                          <div className="border-t border-slate-100 dark:border-slate-700/60 mb-4"></div>

                          {/* Strafen, Ergebnis und Speichern auf einer Ebene */}
                          <div className="flex gap-4">
                            {/* Strafen */}
                            <div className="w-32">
                              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5 h-4">
                                Strafen
                              </label>
                              <div className="flex items-center gap-1">
                                {/* Vorzeichen-Icon (klickbar) */}
                                <button
                                  type="button"
                                  onClick={() => handlePenaltySignChange(identifier)}
                                  disabled={didNotStart}
                                  className={`p-1.5 transition-colors ${didNotStart
                                    ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                    : (athleteData.penaltySign === '-' || (athleteData.penalty || 0) < 0)
                                      ? 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300'
                                      : 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300'
                                    }`}
                                >
                                  <i className={`fas ${(athleteData.penaltySign === '-' || (athleteData.penalty || 0) < 0) ? 'fa-circle-minus' : 'fa-circle-plus'} text-lg`}></i>
                                </button>

                                {/* Penalty-Felder */}
                                {(() => {
                                  const penaltyValue = Math.abs(athleteData.penalty || 0);
                                  const penaltyDigits = getScoreDigits(penaltyValue, true);
                                  const penaltyRefKey1 = `${identifier}-penalty-0`;
                                  const penaltyRefKey2 = `${identifier}-penalty-1`;
                                  const penaltyRefKey3 = `${identifier}-penalty-2`;
                                  const penaltyRef1 = getInputRef(identifier, 'penalty', 0);
                                  const penaltyRef2 = getInputRef(identifier, 'penalty', 1);
                                  const penaltyRef3 = getInputRef(identifier, 'penalty', 2);

                                  return (
                                    <div className="flex items-center gap-0.5">
                                      {/* Feld 1 (Einer) */}
                                      <input
                                        type="number"
                                        min="0"
                                        max="9"
                                        value={penaltyDigits[0] === '' ? '' : penaltyDigits[0]}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          // Erlaube leere Eingabe oder Zahlen 0-9
                                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 9)) {
                                            // Wenn leer, setze auf 0
                                            const digitValue = val === '' ? '0' : val;
                                            handlePenaltyDigitChange(identifier, 0, digitValue, penaltyRefKey2);
                                          }
                                        }}
                                      onKeyDown={(e) => {
                                        // Nur mit Pfeiltasten navigieren; Ziffern sollen den Fokus nicht verändern,
                                        // damit die Eingabe auf Tablets stabil bleibt.
                                        if (e.key === 'ArrowRight') {
                                          setTimeout(() => {
                                            penaltyRef2.current?.focus();
                                            penaltyRef2.current?.select(); // Text markieren
                                          }, 10);
                                        }
                                      }}
                                        disabled={didNotStart}
                                        ref={penaltyRef1}
                                        className={`w-7 px-0.5 py-1.5 text-sm font-semibold text-center border rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${didNotStart
                                          ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-300 dark:border-slate-600'
                                          : (athleteData.penaltySign === '-' || (athleteData.penalty || 0) < 0)
                                            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                                            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500'
                                          }`}
                                      />

                                      {/* Punkt */}
                                      <span className={`text-sm font-bold ${(athleteData.penaltySign === '-' || (athleteData.penalty || 0) < 0)
                                        ? 'text-red-700 dark:text-red-400'
                                        : 'text-green-700 dark:text-green-400'
                                        }`}>.</span>

                                      {/* Feld 2 (Zehntel) */}
                                      <input
                                        type="number"
                                        min="0"
                                        max="9"
                                        value={penaltyDigits[1] === '' ? '' : (penaltyDigits[1] === 0 ? '0' : penaltyDigits[1])}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          // Erlaube leere Eingabe oder Zahlen 0-9
                                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 9)) {
                                            // Wenn leer, setze auf 0
                                            const digitValue = val === '' ? '0' : val;
                                            handlePenaltyDigitChange(identifier, 1, digitValue, penaltyRefKey3);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          // Auch hier: Fokus nur mit Pfeiltasten verschieben,
                                          // nicht bei jeder Ziffern-Eingabe.
                                          if (e.key === 'ArrowRight') {
                                            setTimeout(() => {
                                              penaltyRef3.current?.focus();
                                              penaltyRef3.current?.select(); // Text markieren
                                            }, 10);
                                          } else if (e.key === 'ArrowLeft') {
                                            setTimeout(() => {
                                              penaltyRef1.current?.focus();
                                              penaltyRef1.current?.select(); // Text markieren
                                            }, 10);
                                          }
                                        }}
                                        disabled={didNotStart}
                                        ref={penaltyRef2}
                                        className={`w-7 px-0.5 py-1.5 text-sm font-semibold text-center border rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${didNotStart
                                          ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-300 dark:border-slate-600'
                                          : (athleteData.penaltySign === '-' || (athleteData.penalty || 0) < 0)
                                            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                                            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500'
                                          }`}
                                      />

                                      {/* Feld 3 (Hundertstel) */}
                                      <input
                                        type="number"
                                        min="0"
                                        max="9"
                                        value={penaltyDigits[2] === '' ? '' : (penaltyDigits[2] === 0 ? '0' : penaltyDigits[2])}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          // Erlaube leere Eingabe oder Zahlen 0-9
                                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 9)) {
                                            // Wenn leer, setze auf 0
                                            const digitValue = val === '' ? '0' : val;
                                            handlePenaltyDigitChange(identifier, 2, digitValue, null);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'ArrowLeft') {
                                            setTimeout(() => {
                                              penaltyRef2.current?.focus();
                                              penaltyRef2.current?.select(); // Text markieren
                                            }, 10);
                                          }
                                        }}
                                        disabled={didNotStart}
                                        ref={penaltyRef3}
                                        className={`w-7 px-0.5 py-1.5 text-sm font-semibold text-center border rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${didNotStart
                                          ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-300 dark:border-slate-600'
                                          : (athleteData.penaltySign === '-' || (athleteData.penalty || 0) < 0)
                                            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                                            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500'
                                          }`}
                                      />
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Ergebnis */}
                            <div className="flex-1 flex flex-col items-center">
                              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5 h-4">
                                Ergebnis
                              </label>
                              <div className="text-2xl font-bold text-center text-slate-900 dark:text-slate-100 py-1 tabular-nums">
                                {didNotStart ? '0.00' : (athleteData.finalScore || "0.00")}
                              </div>
                            </div>

                            {/* Speichern Button */}
                            <div className="flex-1 flex flex-col justify-end">
                              <button
                                onClick={() => handleSaveResult(identifier)}
                                disabled={didNotStart || savingParticipants.has(identifier) || !areJudgeScoresValid(athleteData)}
                                className={`w-full ${colors.button} text-white font-semibold py-2 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm`}
                              >
                                {savingParticipants.has(identifier) ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin text-xs"></i>
                                    Speichere...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-check text-xs"></i>
                                    Speichern
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>
        ) : null}

        {/* Gruppenübersicht */}
        {currentEvent ? (
          <div className="mb-8">
            {/* Tabelle als Box */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              {/* Card Header */}
              <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60 flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-list-ol text-xs"></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Gruppen-Übersicht</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Alle Teilnehmer dieser Gruppe</p>
                </div>
              </div>
              {/* Teilnehmerliste oder Partner Teams */}
              {isPartnerForm ? (
                // Partner Teams Anzeige
                partnerTeams.length > 0 ? (() => {
                  const sortedTeams = [...partnerTeams].sort((a, b) => {
                    const aStartNumber = a.startNumber || 999;
                    const bStartNumber = b.startNumber || 999;
                    return aStartNumber - bStartNumber;
                  });

                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-slate-100 dark:divide-slate-700/60">
                        <thead className="bg-slate-50/80 dark:bg-slate-700/30">
                          <tr>
                            <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">#</th>
                            <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Athlet</th>
                            <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Status</th>
                            <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Punkte</th>
                            <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Resultat</th>
                            <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Aktion</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700/60">
                          {sortedTeams.map((team) => {
                            const identifier = `partner-team-${team.id}`;
                            const teamData = editedData.get(identifier) || {
                              judgeScores: [0, 0, 0, 0, 0],
                              penalty: 0,
                              finalScore: 0
                            };
                            const status = participantStatuses.get(identifier) || {};
                            const didNotStart = status.didNotStart || false;
                            const hasResult = savedResults.has(identifier);
                            const isCurrent = (isPartnerForm && (currentAthletes[0]?.id === team.id || currentAthletes[1]?.id === team.id)) ||
                              (!isPartnerForm && (currentAthletes[0]?.id === team.id || currentAthletes[1]?.id === team.id));
                            const nationCode = team.participant1?.nation?.code || 'DE';
                            const startNumber = team.startNumber || 999;
                            const athleteName = `${team.participant1?.name || 'Unbekannt'} & ${team.participant2?.name || 'Unbekannt'}`;
                            const nationName = team.participant1?.nation?.name || 'Nation';

                            // Bestimme Status
                            let statusText = '';
                            let statusColor = '';

                            if (didNotStart) {
                              statusText = 'DNS';
                              statusColor = 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-400/30 dark:border-slate-500/30';
                            } else if (hasResult) {
                              statusText = 'BEWERTET';
                              statusColor = 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-700/30 dark:border-emerald-300/30';
                            } else if (isCurrent) {
                              const isBlue = (isPartnerForm ? currentAthletes[0]?.id === team.id : currentAthletes[0]?.id === team.id);
                              statusText = `AKTIV • ${isBlue ? 'BAHN A' : 'BAHN B'}`;
                              statusColor = isBlue
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-700/30 dark:border-blue-300/30'
                                : 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border border-violet-700/30 dark:border-violet-300/30';
                            } else {
                              statusText = 'WARTEND';
                              statusColor = 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-600/30 dark:border-slate-400/30';
                            }

                            return (
                              <tr
                                key={team.id}
                                className={`${didNotStart ? 'opacity-60 grayscale' : 'hover:bg-slate-50/50 dark:hover:bg-slate-700/30'} transition-colors`}
                              >
                                <td className="px-5 py-3 whitespace-nowrap">
                                  <span className="inline-flex items-center justify-center w-7 h-7 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-600 font-semibold text-xs tabular-nums">
                                    {startNumber}
                                  </span>
                                </td>
                                <td className="px-5 py-3 whitespace-nowrap">
                                  <div className="flex items-center gap-2.5">
                                    <img
                                      src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                      alt={nationName}
                                      className={`w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0 ${didNotStart ? 'opacity-60' : ''}`}
                                    />
                                    <div>
                                      <div className={`text-sm font-medium ${didNotStart ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>
                                        {athleteName}
                                      </div>
                                      <div className={`text-xs ${didNotStart ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                        {nationName}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-5 py-3 whitespace-nowrap">
                                  {statusText && (
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                      {statusText}
                                    </span>
                                  )}
                                </td>
                                <td className="px-5 py-3">
                                  {hasResult && teamData.judgeScores.some(s => s > 0) ? (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      {teamData.judgeScores.map((score, idx) => {
                                        const scoreValue = parseFloat(score) || 0;
                                        return (
                                          <div key={idx} className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-0.5">
                                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                                              {scoreValue.toFixed(2)}
                                            </span>
                                          </div>
                                        );
                                      })}
                                      {(() => {
                                        const penaltyValue = parseFloat(teamData.penalty) || 0;
                                        if (penaltyValue === 0) return null;
                                        const isNegative = penaltyValue < 0;
                                        return (
                                          <div className={`rounded-lg px-2 py-0.5 border ${isNegative ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700' : 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700'}`}>
                                            <span className={`text-xs font-semibold tabular-nums ${isNegative ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                              {isNegative ? '-' : '+'}{Math.abs(penaltyValue).toFixed(2)}
                                            </span>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                                      {didNotStart ? '—' : 'Ausstehend'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-5 py-3 whitespace-nowrap">
                                  <span className={`text-sm font-bold tabular-nums ${hasResult ? 'text-slate-900 dark:text-slate-100' : 'text-slate-300 dark:text-slate-600'}`}>
                                    {hasResult ? (teamData.finalScore || "0.00") : '—'}
                                  </span>
                                </td>
                                <td className="px-5 py-3 whitespace-nowrap">
                                  {hasResult ? (
                                    <button
                                      onClick={() => {
                                        updateManualSelection(true);
                                        setCurrentAthletes([team, currentAthletes[1]]);
                                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
                                      }}
                                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/50 dark:hover:border-primary-light/50 transition-colors"
                                      title="Bearbeiten"
                                    >
                                      <i className="fas fa-pencil-alt text-[10px]"></i>
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Footer-Zeile */}
                          <tr className="bg-slate-50/80 dark:bg-slate-700/20">
                            <td colSpan="6" className="px-5 py-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <i className="fas fa-user-tie text-xs text-slate-400 dark:text-slate-500"></i>
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {user?.name || user?.username || 'Recorder'}
                                  </span>
                                </div>
                                <button
                                  onClick={() => setShowCompleteDialog(true)}
                                  disabled={isSaving || !doAllAthletesHaveResults()}
                                  className="inline-flex items-center gap-2 h-9 px-5 bg-emerald-500 dark:bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                >
                                  {isSaving ? (
                                    <>
                                      <i className="fas fa-spinner fa-spin text-xs"></i>
                                      {isAdminEdit ? 'Wird aktualisiert…' : 'Wird abgeschlossen…'}
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-check-circle text-xs"></i>
                                      {isAdminEdit ? 'Gruppe aktualisieren' : 'Gruppe abschließen'}
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })() : (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-users text-slate-400 dark:text-slate-500 text-lg"></i>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {t("scorekeeper.noParticipants", "Keine Partner Teams für dieses Event gefunden")}
                    </p>
                  </div>
                )
              ) : participants.length > 0 ? (() => {
                // Sortiere alle Teilnehmer nach geplanter Reihenfolge (Startnummer), unabhängig von DNS-Status
                const sortedParticipants = [...participants].sort((a, b) => {
                  const aGroup = a.groups?.find(g => g.id === currentEvent.group.id);
                  const bGroup = b.groups?.find(g => g.id === currentEvent.group.id);
                  const aStartNumber = aGroup ? aGroup.startNumber : 999;
                  const bStartNumber = bGroup ? bGroup.startNumber : 999;

                  // Sortiere nur nach Startnummer (geplante Reihenfolge)
                  return aStartNumber - bStartNumber;
                });

                // Trenne Nachzügler von der geplanten Reihenfolge
                const plannedOrderParticipants = sortedParticipants.filter(p => {
                  const status = participantStatuses.get(p.id) || {};
                  return !status.isLate; // Alle Teilnehmer, die nicht nachträglich gekommen sind
                });

                const lateParticipants = sortedParticipants.filter(p => {
                  const status = participantStatuses.get(p.id) || {};
                  return status.isLate; // Nur Nachzügler
                });

                return (
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-slate-100 dark:divide-slate-700/60">
                      <thead className="bg-slate-50/80 dark:bg-slate-700/30">
                        <tr>
                          <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">#</th>
                          <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Athlet</th>
                          <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Status</th>
                          <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Punkte</th>
                          <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Resultat</th>
                          <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700/60">
                        {/* Teilnehmer in geplanter Reihenfolge (inklusive DNS) */}
                        {plannedOrderParticipants.map((participant, index) => {
                          const participantData = editedData.get(participant.id) || {
                            judgeScores: [0, 0, 0, 0, 0],
                            penalty: 0,
                            finalScore: 0
                          };
                          const status = participantStatuses.get(participant.id) || {};
                          const didNotStart = status.didNotStart || false;
                          const hasResult = savedResults.has(participant.id);
                          const isCurrent = !isPartnerForm && (currentAthletes[0]?.id === participant.id || currentAthletes[1]?.id === participant.id);
                          const nationCode = participant.nation?.code || participant.nationCode || 'DE';

                          // Hole Startnummer für die Anzeige
                          const participantGroup = participant.groups?.find(g => g.id === currentEvent.group.id);
                          const startNumber = participantGroup ? participantGroup.startNumber : index + 1;

                          // Bestimme Status
                          let statusText = '';
                          let statusColor = '';

                          if (didNotStart) {
                            statusText = 'DNS';
                            statusColor = 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-400/30 dark:border-slate-500/30';
                          } else if (hasResult) {
                            statusText = 'BEWERTET';
                            statusColor = 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-700/30 dark:border-emerald-300/30';
                          } else if (isCurrent) {
                            const isBlue = !isPartnerForm && currentAthletes[0]?.id === participant.id;
                            statusText = `AKTIV • ${isBlue ? 'BAHN A' : 'BAHN B'}`;
                            statusColor = isBlue
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-700/30 dark:border-blue-300/30'
                              : 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border border-violet-700/30 dark:border-violet-300/30';
                          } else {
                            statusText = 'WARTEND';
                            statusColor = 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-600/30 dark:border-slate-400/30';
                          }

                          return (
                            <tr
                              key={participant.id}
                              className={`${didNotStart ? 'opacity-60 grayscale' : 'hover:bg-slate-50/50 dark:hover:bg-slate-700/30'} transition-colors`}
                            >
                              <td className="px-5 py-3 whitespace-nowrap">
                                <span className="inline-flex items-center justify-center w-7 h-7 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-600 font-semibold text-xs tabular-nums">
                                  {startNumber}
                                </span>
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2.5">
                                  <img
                                    src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                    alt={participant.nation?.name || participant.nation}
                                    className={`w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0 ${didNotStart ? 'opacity-60' : ''}`}
                                  />
                                  <div>
                                    <div className={`text-sm font-medium ${didNotStart ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>
                                      {participant.name}
                                    </div>
                                    <div className={`text-xs ${didNotStart ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                      {participant.nation?.name || participant.nation || ''}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap">
                                {statusText && (
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                    {statusText}
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3">
                                {hasResult && participantData.judgeScores.some(s => s > 0) ? (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {participantData.judgeScores.map((score, idx) => {
                                      const scoreValue = parseFloat(score) || 0;
                                      return (
                                        <div key={idx} className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-0.5">
                                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                                            {scoreValue.toFixed(2)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                    {(() => {
                                      const penaltyValue = parseFloat(participantData.penalty) || 0;
                                      if (penaltyValue === 0) return null;
                                      const isNegative = penaltyValue < 0;
                                      return (
                                        <div className={`rounded-lg px-2 py-0.5 border ${isNegative ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700' : 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700'}`}>
                                          <span className={`text-xs font-semibold tabular-nums ${isNegative ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                            {isNegative ? '-' : '+'}{Math.abs(penaltyValue).toFixed(2)}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                                    {didNotStart ? '—' : 'Ausstehend'}
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap">
                                <span className={`text-sm font-bold tabular-nums ${hasResult ? 'text-slate-900 dark:text-slate-100' : 'text-slate-300 dark:text-slate-600'}`}>
                                  {hasResult ? (participantData.finalScore || "0.00") : '—'}
                                </span>
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap">
                                {hasResult ? (
                                  <button
                                    onClick={() => {
                                      updateManualSelection(true);
                                      setCurrentAthletes([participant, currentAthletes[1]]);
                                      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
                                    }}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/50 dark:hover:border-primary-light/50 transition-colors"
                                    title="Bearbeiten"
                                  >
                                    <i className="fas fa-pencil-alt text-[10px]"></i>
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Nachzügler-Sektion */}
                        {lateParticipants.length > 0 && (
                          <>
                            <tr>
                              <td colSpan="6" className="px-5 py-2 bg-amber-50/60 dark:bg-amber-900/10 border-t border-amber-200/60 dark:border-amber-700/30">
                                <div className="flex items-center gap-1.5">
                                  <i className="fas fa-clock text-amber-500 dark:text-amber-400 text-[10px]"></i>
                                  <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-400">
                                    {t("scorekeeper.lateParticipants", "Nachzügler")}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {lateParticipants.map((participant, index) => {
                              const identifier = participant.id;
                              const participantData = editedData.get(identifier) || {
                                judgeScores: [0, 0, 0, 0, 0],
                                penalty: 0,
                                finalScore: 0
                              };
                              const status = participantStatuses.get(identifier) || {};
                              const didNotStart = status.didNotStart || false;
                              const hasResult = savedResults.has(identifier);
                              const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                              const isCurrent = !isPartnerForm && (currentAthletes[0]?.id === participant.id || currentAthletes[1]?.id === participant.id);

                              // Status-Logik wie in der Haupttabelle, aber mit "VERSPÄTET" als Wartestatus
                              let statusText = '';
                              let statusColor = '';

                              if (didNotStart) {
                                statusText = 'DNS';
                                statusColor = 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-400/30 dark:border-slate-500/30';
                              } else if (hasResult) {
                                statusText = 'BEWERTET';
                                statusColor = 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-700/30 dark:border-emerald-300/30';
                              } else if (isCurrent) {
                                const isBlue = !isPartnerForm && currentAthletes[0]?.id === participant.id;
                                statusText = `AKTIV • ${isBlue ? 'BAHN A' : 'BAHN B'}`;
                                statusColor = isBlue
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-700/30 dark:border-blue-300/30'
                                  : 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border border-violet-700/30 dark:border-violet-300/30';
                              } else {
                                statusText = 'VERSPÄTET';
                                statusColor = 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-700/30 dark:border-yellow-300/30';
                              }

                              return (
                                <tr key={participant.id} className="bg-amber-50/20 dark:bg-amber-900/5 hover:bg-amber-50/40 dark:hover:bg-amber-900/10 transition-colors">
                                  <td className="px-5 py-3 whitespace-nowrap">
                                    <span className="inline-flex items-center justify-center w-7 h-7 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg border border-amber-200/60 dark:border-amber-700/40 font-semibold text-xs tabular-nums">
                                      {plannedOrderParticipants.length + index + 1}
                                    </span>
                                  </td>
                                  <td className="px-5 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2.5">
                                      <img
                                        src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                        alt={participant.nation?.name || participant.nation}
                                        className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                                      />
                                      <div>
                                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                          {participant.name}
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                          {participant.nation?.name || participant.nation}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-5 py-3 whitespace-nowrap">
                                    {statusText && (
                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                        {statusText}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-5 py-3">
                                    {hasResult && participantData.judgeScores.some(s => s > 0) ? (
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        {participantData.judgeScores.map((score, idx) => {
                                          const scoreValue = parseFloat(score) || 0;
                                          return (
                                            <div key={idx} className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-0.5">
                                              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                                                {scoreValue.toFixed(2)}
                                              </span>
                                            </div>
                                          );
                                        })}
                                        {(() => {
                                          const penaltyValue = parseFloat(participantData.penalty) || 0;
                                          if (penaltyValue === 0) return null;
                                          const isNegative = penaltyValue < 0;
                                          return (
                                            <div className={`rounded-lg px-2 py-0.5 border ${isNegative ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700' : 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700'}`}>
                                              <span className={`text-xs font-semibold tabular-nums ${isNegative ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                                {isNegative ? '-' : '+'}{Math.abs(penaltyValue).toFixed(2)}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                                        {didNotStart ? '—' : 'Ausstehend'}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-5 py-3 whitespace-nowrap">
                                    <span className={`text-sm font-bold tabular-nums ${hasResult ? 'text-slate-900 dark:text-slate-100' : 'text-slate-300 dark:text-slate-600'}`}>
                                      {hasResult ? (participantData.finalScore || "0.00") : '—'}
                                    </span>
                                  </td>
                                  <td className="px-5 py-3 whitespace-nowrap">
                                    {hasResult ? (
                                      <button
                                        onClick={() => {
                                          updateManualSelection(true);
                                          setCurrentAthletes([participant, currentAthletes[1]]);
                                          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
                                        }}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/50 dark:hover:border-primary-light/50 transition-colors"
                                        title="Bearbeiten"
                                      >
                                        <i className="fas fa-pencil-alt text-[10px]"></i>
                                      </button>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        )}
                        {/* Footer-Zeile */}
                        {(participants.length > 0 || partnerTeams.length > 0) && (
                          <tr className="bg-slate-50/80 dark:bg-slate-700/20">
                            <td colSpan="6" className="px-5 py-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <i className="fas fa-user-tie text-xs text-slate-400 dark:text-slate-500"></i>
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {user?.name || user?.username || 'Recorder'}
                                  </span>
                                </div>
                                <button
                                  onClick={() => setShowCompleteDialog(true)}
                                  disabled={isSaving || !doAllAthletesHaveResults()}
                                  className="inline-flex items-center gap-2 h-9 px-5 bg-emerald-500 dark:bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                >
                                  {isSaving ? (
                                    <>
                                      <i className="fas fa-spinner fa-spin text-xs"></i>
                                      Wird abgeschlossen…
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-check-circle text-xs"></i>
                                      Gruppe abschließen
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })() : (
                <div className="text-center py-12">
                  <p className="text-slate-500 dark:text-slate-400">
                    {t("scorekeeper.noParticipants", "Keine Teilnehmer für dieses Event gefunden")}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-8 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-clock text-slate-400 dark:text-slate-500 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1.5">
              {t("scorekeeper.noActiveEvent", "Kein aktives Event")}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("scorekeeper.noActiveEventDesc", "Es läuft derzeit kein Event auf dieser Fläche")}
            </p>
          </div>
        )}

        {/* Nächste Events */}
        {upcomingEventsWithCounts.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-2.5 mb-4">
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
                {t("scorekeeper.upcomingEvents", "Kommende Events")}
              </h2>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                {upcomingEventsWithCounts.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {upcomingEventsWithCounts.map((event) => {
                const isPrep = event.status === 'Vorbereitung';
                return (
                  <div
                    key={event.id}
                    className={`bg-white dark:bg-slate-800 rounded-2xl border border-l-4 shadow-sm overflow-hidden ${
                      isPrep
                        ? 'border-slate-200 dark:border-slate-700 border-l-orange-400'
                        : 'border-slate-200 dark:border-slate-700 border-l-yellow-400'
                    }`}
                  >
                    <div className="px-4 py-3.5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug">
                          {(() => {
                            const fullName = event.group?.name || event.category || 'Unbekannt';
                            const dashIndex = fullName.indexOf('-');
                            return dashIndex > 0 ? fullName.substring(0, dashIndex).trim() : fullName;
                          })()}
                        </h3>
                        <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                          isPrep
                            ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700/40'
                            : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/40'
                        }`}>
                          <i className={`fas ${isPrep ? 'fa-hourglass-half' : 'fa-clock'} text-[9px]`}></i>
                          {isPrep ? t("scorekeeper.status.preparation", "Vorbereitung") : t("scorekeeper.status.pending", "Ausstehend")}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <i className="fas fa-clock text-[9px]"></i>
                          {formatTime(event.startTime)} – {formatTime(event.endTime)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="fas fa-users text-[9px]"></i>
                          {event.participantCount || 0} Teilnehmer
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: "", type: "info" })}
        />
      </div>

      {/* Bestätigungsdialog für Gruppe abschließen */}
      <ConfirmationModal
        isOpen={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
        onConfirm={async () => {
          await handleCompleteGroup();
        }}
        title={isAdminEdit ? "Gruppe aktualisieren" : "Gruppe abschließen"}
        subtitle={isAdminEdit ? "Die Änderungen werden gespeichert." : "Diese Aktion kann nicht rückgängig gemacht werden."}
        message={isAdminEdit ? "Möchten Sie die Änderungen wirklich speichern?" : "Möchten Sie die Gruppe wirklich abschließen?"}
        confirmText={isAdminEdit ? "Aktualisieren" : "Abschließen"}
        cancelText="Abbrechen"
        type="warning"
        layout="inline"
        isLoading={isSaving}
      />
    </TailwindPageWrapper>
  );
};

export default ScorekeeperView;

